import type { ShopifyAjaxCartData, ShopifyAjaxCartLineItem } from "../../models/shopify/ajax-api/cart"
import { STORY_OF_US_CARD_HANDLE } from "../../models/story-of-us"
import type { TemplateIds } from "../../models/uv-inserts/template-ids"

/**
 * Normalises cart `_personalisation_choices` to an array for inspection.
 * Shopify Cart AJAX line item properties are string values; `_personalisation_choices` is a JSON string of the choices array.
 * Array input is still accepted for tests and any non-standard callers.
 */
function parsePersonalisationChoicesRaw(raw: unknown): unknown[] {
  if (typeof raw === "string" && raw.trim() !== "") {
    try {
      const parsed: unknown = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  if (Array.isArray(raw)) {
    return raw
  }
  return []
}

/**
 * Collects numeric variant IDs that appear in UV_INSERT choice rows (`c` is `configHandle:variantId`).
 */
function collectUvInsertVariantIdsFromChoices(choices: unknown[]): Set<string> {
  const ids = new Set<string>()
  for (const row of choices) {
    if (!row || typeof row !== "object") {
      continue
    }
    const rec = row as Record<string, unknown>
    const c = rec.c
    if (typeof c !== "string" || c.trim() === "") {
      continue
    }
    const parts = c.split(":")
    const tail = parts[parts.length - 1]
    if (/^\d+$/.test(tail)) {
      ids.add(tail)
    }
  }
  return ids
}

/**
 * The piece's gift wrap key: its own `_giftwrap_key`, else the one in `_personalisation_base_attributes`
 * (a JSON string on /cart.js), which is where a Story of Us piece carries its card's key.
 */
function getPieceGiftWrapKey(properties: Record<string, unknown>): string | null {
  const own = properties["_giftwrap_key"]
  if (typeof own === "string" && own.trim() !== "") {
    return own
  }
  const raw = properties["_personalisation_base_attributes"]
  let baseAttributes: unknown = raw
  if (typeof raw === "string") {
    try {
      baseAttributes = JSON.parse(raw)
    } catch {
      return null
    }
  }
  if (!baseAttributes || typeof baseAttributes !== "object") {
    return null
  }
  const key = (baseAttributes as Record<string, unknown>)["_giftwrap_key"]
  return typeof key === "string" && key.trim() !== "" ? key : null
}

/**
 * Whether the cart holds the Story of Us keepsake card line linked to this piece by its gift wrap key
 * (ALBAU-909: the card is its own line, no longer one of the piece's choices).
 */
function hasStoryOfUsCardLine(items: ShopifyAjaxCartLineItem[], giftWrapKey: string): boolean {
  return items.some(
    (item) =>
      String(item.handle ?? "").toLowerCase() === STORY_OF_US_CARD_HANDLE &&
      String(item.properties?.["_giftwrap_key"] ?? "") === giftWrapKey,
  )
}

/**
 * Returns true when the cart line for `variantId` has personalisation choices that reference
 * a variant ID present in the UV inserts `templateIds` map (Made Mine insert already chosen),
 * or when its Story of Us keepsake card is in the cart as its own line.
 */
export function checkCartForUvInsert(options: {
  cart: ShopifyAjaxCartData
  variantId: string
  templateIds: TemplateIds
}): boolean {
  const { cart, variantId, templateIds } = options
  const target = String(variantId).trim()
  if (!target) {
    return false
  }

  const line = cart.items.find((item) => String(item.variant_id) === target)
  if (!line?.properties) {
    return false
  }

  const pieceGiftWrapKey = getPieceGiftWrapKey(line.properties)
  if (pieceGiftWrapKey && hasStoryOfUsCardLine(cart.items, pieceGiftWrapKey)) {
    return true
  }

  const rawChoices = line.properties["_personalisation_choices"]
  const choices = parsePersonalisationChoicesRaw(rawChoices)
  if (choices.length === 0) {
    return false
  }

  const templateVariantKeys = new Set(
    Object.keys(templateIds).map((k) => String(k)),
  )
  if (templateVariantKeys.size === 0) {
    return false
  }

  const choiceVariantIds = collectUvInsertVariantIdsFromChoices(choices)
  for (const id of choiceVariantIds) {
    if (templateVariantKeys.has(id)) {
      return true
    }
  }

  return false
}
